/**
 * [Phase 2b-C §5.1 R2] Step 2 - Roster Construction (full).
 *
 * Extends the 2b-B basic version with the "Additional Roster Positions"
 * section. Per R2, TD-35 stays Deferred Phase 6: all 7 rows are DISABLED
 * scaffolding. No state mutation, no persistence of non-zero values. Code
 * present so Phase 6 just flips the disabled flag.
 *
 * Validation unchanged: total >= 8, QB >= 1, at least one of {RB, WR, TE} >= 1.
 */
import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

// `max` defaults to STARTER_SLOT_MAX. Bench carries its own ceiling: deep
// benches (dynasty / devy / best-ball formats) need far more depth than a
// starting slot ever would. Roster size — and therefore the number of draft
// ROUNDS — is just the sum of these, so raising the bench ceiling raises the
// round count with no other wiring (see BENCH_SLOT_MAX note below).
const STARTER_SLOT_MAX = 9;
// Rounds = roster_size = sum(all slots). At 99 bench a 12-team league drafts
// ~1,300 players, which the pool can supply (~2,000 draftable). The board,
// round progress bar and on-deck math all size themselves off roster_size,
// so nothing else needs a matching bump.
const BENCH_SLOT_MAX = 99;

// Measured against the live player table (2026-08-02): players on a real NFL
// team, with 2025 metrics and a Sleeper mapping, at QB/RB/WR/TE/K/DEF — the
// exact filter the engine's auto-pick uses for a standard league. Teams x
// roster_size beyond this and the draft runs out of players mid-round, so the
// wizard warns instead of letting a live draft die at the board. Re-measure if
// the player ingest changes.
const DRAFTABLE_SKILL_POOL = 730;

const STANDARD_POSITIONS = [
  { key: "qb", label: "QB", desc: "Quarterback" },
  { key: "rb", label: "RB", desc: "Running Back" },
  { key: "wr", label: "WR", desc: "Wide Receiver" },
  { key: "te", label: "TE", desc: "Tight End" },
  { key: "flex", label: "FLEX", desc: "RB / WR / TE" },
  { key: "superflex", label: "SUPERFLEX", desc: "QB / RB / WR / TE" },
  { key: "k", label: "K", desc: "Kicker" },
  { key: "def", label: "DEF", desc: "Defense / Special Teams" },
  { key: "bench", label: "BENCH", desc: "Bench slots", max: BENCH_SLOT_MAX },
];

// [TD-35 retires in v1.4-expanded] All 7 IDP/extended rows go live with the
// same Stepper widget the standard rows use. Persistence already wired via
// the form.roster → POST → league.settings.roster path (see TD-119 v1.4
// downstream wiring of pool tabs + endpoint + CPU bots).
const ADDITIONAL_POSITIONS = [
  { key: "st", label: "ST", desc: "Special Teams" },
  { key: "s", label: "S", desc: "Safety (IDP)" },
  { key: "cb", label: "CB", desc: "Cornerback (IDP)" },
  { key: "lb", label: "LB", desc: "Linebacker (IDP)" },
  { key: "dl", label: "DL", desc: "Defensive Line (IDP)" },
  { key: "ol", label: "OL", desc: "Offensive Line" },
  { key: "fb", label: "FB", desc: "Fullback" },
];

const Stepper = ({ value, onChange, min = 0, max = STARTER_SLOT_MAX, disabled, dataTestId }) => (
  <div className="flex items-center gap-2">
    <button
      type="button"
      onClick={() => onChange(Math.max(min, value - 1))}
      disabled={disabled || value <= min}
      data-testid={`${dataTestId}-decrement`}
      className="w-7 h-7 rounded font-bold text-sm bg-[#252525] hover:bg-[#333] text-[#F5F5F5] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
    >
      −
    </button>
    <span data-testid={`${dataTestId}-value`} className="w-8 text-center font-mono font-bold text-[#F5F5F5]">
      {value}
    </span>
    <button
      type="button"
      onClick={() => onChange(Math.min(max, value + 1))}
      disabled={disabled || value >= max}
      data-testid={`${dataTestId}-increment`}
      className="w-7 h-7 rounded font-bold text-sm bg-[#252525] hover:bg-[#333] text-[#F5F5F5] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
    >
      +
    </button>
  </div>
);

export default function Step2Roster({ form, update }) {
  const [expanded, setExpanded] = useState(false);
  const roster = form.roster;
  const total = Object.values(roster).reduce((a, b) => a + b, 0);
  const setSlot = (key, val) =>
    update({ roster: { ...roster, [key]: val } });

  // A deep bench can ask for more picks than there are players to pick. The
  // auto-pick only scans the positions the league actually rosters, so a
  // standard league is bounded by the skill pool, not the full player table.
  const teamCount = form.num_teams || 12;
  const idpEnabled = ADDITIONAL_POSITIONS.some(({ key }) => (roster[key] ?? 0) > 0);
  const poolShortfall = !idpEnabled && teamCount * total > DRAFTABLE_SKILL_POOL;

  return (
    <div data-testid="wizard-step-2" className="space-y-8">
      <header>
        <h2 className="font-display font-black text-3xl uppercase text-[#F5F5F5]">
          Roster Construction
        </h2>
        <p className="font-body text-sm text-[#A0A0A0] mt-1">
          Configure your league's lineup structure.
        </p>
      </header>

      <section>
        <h3 className="font-display font-bold text-sm uppercase tracking-wider text-[#A0A0A0] mb-3">
          Standard Roster Positions
        </h3>
        <div className="bg-[#1C1C1C] border border-[#252525] rounded-xl divide-y divide-[#252525]">
          {STANDARD_POSITIONS.map(({ key, label, desc, max }) => (
            <div key={key} className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="font-display font-bold text-base uppercase tracking-wide text-[#F5F5F5]">
                  {label}
                </p>
                <p className="font-body text-xs text-[#A0A0A0]">{desc}</p>
              </div>
              <Stepper
                value={roster[key] ?? 0}
                onChange={(v) => setSlot(key, v)}
                max={max ?? STARTER_SLOT_MAX}
                dataTestId={`wizard-roster-${key}`}
              />
            </div>
          ))}
        </div>
      </section>

      {/* [§5.1 R2] Additional Roster Positions - all 7 DISABLED in 2b. */}
      <section>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="wizard-additional-positions-toggle"
          className="w-full flex items-center justify-between bg-[#1C1C1C] border border-[#252525] rounded-xl px-4 py-3 hover:border-[#FF6B00]/30 transition-colors"
        >
          <span className="flex items-center gap-2">
            {expanded ? <ChevronDown size={16} className="text-[#A0A0A0]" /> : <ChevronRight size={16} className="text-[#A0A0A0]" />}
            <span className="font-display font-bold text-sm uppercase tracking-wider text-[#F5F5F5]">
              Additional Roster Positions
            </span>
          </span>
        </button>

        {expanded && (
          <div
            data-testid="wizard-additional-positions-panel"
            className="mt-2 bg-[#1C1C1C]/60 border border-dashed border-[#252525] rounded-xl divide-y divide-[#252525]/60"
          >
            {ADDITIONAL_POSITIONS.map(({ key, label, desc }) => (
              <div
                key={key}
                data-testid={`wizard-additional-position-${key}`}
                className="flex items-center justify-between px-4 py-3"
              >
                <div>
                  <p className="font-display font-bold text-base uppercase tracking-wide text-[#F5F5F5]">
                    {label}
                  </p>
                  <p className="font-body text-xs text-[#A0A0A0]">{desc}</p>
                </div>
                <Stepper
                  value={roster[key] ?? 0}
                  onChange={(v) => setSlot(key, v)}
                  dataTestId={`wizard-roster-${key}`}
                />
              </div>
            ))}
            <div className="px-4 py-3">
              <p className="font-body text-[11px] text-[#A0A0A0] italic">
                IDP and special-teams positions feed the player pool filter and CPU bot roster-aware logic. Leave at 0 for standard leagues.
              </p>
            </div>
          </div>
        )}
      </section>

      <section className="bg-[#1C1C1C] border border-[#252525] rounded-xl px-4 py-3">
        <p className="font-body text-sm text-[#A0A0A0]">
          Total roster spots:{" "}
          <span data-testid="wizard-roster-total" className="font-display font-black text-lg text-[#F5F5F5]">
            {total}
          </span>
          <span className="mx-2 opacity-40">·</span>
          <span data-testid="wizard-roster-rounds" className="font-display font-black text-lg text-[#F5F5F5]">
            {total}
          </span>{" "}
          draft {total === 1 ? "round" : "rounds"}
        </p>
        {poolShortfall && (
          <p
            data-testid="wizard-roster-pool-warning"
            className="font-body text-xs text-[#FFB020] mt-2 leading-relaxed"
          >
            At {teamCount} teams that's {teamCount * total} total picks, but only about{" "}
            {DRAFTABLE_SKILL_POOL} draftable QB/RB/WR/TE/K/DEF players exist — the draft
            would run dry around round {Math.floor(DRAFTABLE_SKILL_POOL / teamCount)}.
            Trim the bench, or open Additional Roster Positions to draft IDP/OL too.
          </p>
        )}
      </section>
    </div>
  );
}

export const validateStep2 = (form) => {
  const r = form.roster;
  const total = Object.values(r).reduce((a, b) => a + b, 0);
  if (total < 8) return "Total roster spots must be at least 8.";
  if ((r.qb ?? 0) < 1) return "At least 1 QB slot is required.";
  if ((r.rb ?? 0) + (r.wr ?? 0) + (r.te ?? 0) < 1)
    return "At least one of RB/WR/TE is required.";
  return null;
};
